import Button from '@material-ui/core/Button';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { Theme } from '@material-ui/core/styles';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import AutorenewIcon from '@material-ui/icons/Autorenew';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import StraightenIcon from '@material-ui/icons/Straighten';
import SwapHorizontalCircleIcon from '@material-ui/icons/SwapHorizontalCircle';
import { makeStyles } from '@material-ui/styles';
import { FunctionComponent, memo, useRef, useState } from 'react';
import { useAuthUser } from '../../contexts/AuthProvider/hooks';
import { FilterMenuItem } from './components/ChangeMinimumWeightFilter/ChangeMinimumWeightFilter';
import ExportToCsv from '../../pages/Devices/components/ExporToCsv/ExportToCsv';
import NoTargetIcon from '@material-ui/icons/DeviceHub';
import { Props } from './types';
import { getActionItemsState } from './config';
import LinkIcon from '@material-ui/icons/Link';
import {
  Archive,
  FlagOutlined,
  VerticalAlignTop,
  Block,
  Delete,
  Replay,
  RemoveCircleOutline,
  FileCopy,
} from '@material-ui/icons';

import { Divider } from '@material-ui/core';
import { useAppDispatch } from '../../hooks';
import { setActionMenuDevices } from '../../store/app-slice';
import TestDeviceSwitch from './components/TestDeviceSwitch/TestDeviceSwitch';
import { useSnackbar } from 'notistack';
import { isMuted } from '../../pages/Devices/config';
import { isTotalMeasurementCount } from '../../utils/helpers';
import StockDeviceSwitch from './components/StockDeviceSwitch/StockDeviceSwitch';
import UnlinkedGatewaySwitch from './components/UnlinkedGatewaySwitch/UnlinkedGatewaySwitch';

const useStyles = makeStyles((theme: Theme) => ({
  icon: {
    marginRight: theme.spacing(1),
  },
}));

const ActionsMenu: FunctionComponent<Props> = memo(
  ({ devices, buttonIcon, updateDialogState: updateState }): any => {
    const classes = useStyles();
    const [isOpen, setIsOpen] = useState(false);
    const { authUser } = useAuthUser();
    const anchorElement = useRef<HTMLElement>();
    const dispatchRedux = useAppDispatch();

    const actionItemState = getActionItemsState(authUser, devices);
    const isAnyUnpinned = !!(devices && devices.find((d) => !d.isPinned));
    const isAnyUnarchived = !!(devices && devices.find((d) => !d.isArchived));
    const isAnyMuted = !!(devices && devices.find((d) => isMuted(d)));
    const isBlocked = !!(devices && devices.find((d) => d.deactivated));

    const { enqueueSnackbar } = useSnackbar();
    const totalMeasurementMode = isTotalMeasurementCount();

    const updateDialogState = (action: string) => {
      dispatchRedux(setActionMenuDevices(devices));
      updateState(action);
    };
    return (
      <>
        <Button
          color="primary"
          innerRef={anchorElement}
          aria-owns={isOpen ? 'actions-menu' : undefined}
          aria-haspopup="true"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setIsOpen(true);
          }}
        >
          {buttonIcon ? (
            buttonIcon
          ) : (
            <>
              Actions <KeyboardArrowDownIcon />
            </>
          )}
        </Button>
        <Menu
          id="actions-menu"
          anchorEl={anchorElement.current}
          open={isOpen}
          onClose={() => setIsOpen(false)}
        >
          {/*<VisibleTo global={[Role.DeviceManager]} org={[Role.DeviceManager]}>*/}
          {actionItemState.showChangeTimeZone && (
            <MenuItem
              disabled={!actionItemState.canChangeTimeZone}
              onClick={() => {
                updateDialogState('OPEN_CHANGE_TIME_ZONE');
                setIsOpen(false);
              }}
            >
              <AccessTimeIcon className={classes.icon} />
              Change time zone
            </MenuItem>
          )}
          {actionItemState.showChangeWeightUnit && (
            <MenuItem
              disabled={!actionItemState.canChangeWeightUnit}
              onClick={() => {
                updateDialogState('OPEN_CHANGE_WEIGHT_UNIT');
                setIsOpen(false);
              }}
            >
              <StraightenIcon className={classes.icon} />
              Change weight unit
            </MenuItem>
          )}
          <FilterMenuItem
            disabled={!actionItemState.canChangeMinimumWeightFilter}
            shouldShowItem={!!actionItemState.showChangeMinimumWeightFilter}
            onEdit={() => {
              updateDialogState('OPEN_WEIGHT_FILTER');
              setIsOpen(false);
            }}
          />
          {actionItemState.showConfigureSync && (
            <MenuItem
              disabled={!actionItemState.canConfigureSync}
              onClick={() => {
                updateDialogState('OPEN_CONFIGURE_SYNC');
                setIsOpen(false);
              }}
            >
              <AutorenewIcon className={classes.icon} />
              Configure sync
            </MenuItem>
          )}
          {actionItemState.showResetToken && (
            <MenuItem
              disabled={!actionItemState.canResetToken}
              onClick={() => {
                updateDialogState('OPEN_RESET_TOKEN');
                setIsOpen(false);
              }}
            >
              <LockOpenIcon className={classes.icon} />
              Reset token
            </MenuItem>
          )}

          <MenuItem
            disabled={!actionItemState.canChangeTargetSystems}
            onClick={() => {
              updateDialogState('CHANGE_TARGET_SYSTEM');
              setIsOpen(false);
            }}
          >
            <NoTargetIcon className={classes.icon} />
            Change webhooks
          </MenuItem>

          <MenuItem
            disabled={!actionItemState.showTransferOwnership}
            onClick={() => {
              updateDialogState('TRANSFER_OWNERSHIP');
              setIsOpen(false);
            }}
          >
            <SwapHorizontalCircleIcon className={classes.icon} />
            Move device
          </MenuItem>

          {actionItemState.showUnlinkDevices && (
            <MenuItem
              disabled={!actionItemState.canUnlinkDevices}
              onClick={() => {
                updateDialogState('DEVICE_LINKING');
                setIsOpen(false);
              }}
            >
              <LinkIcon className={classes.icon} />
              Device linking
            </MenuItem>
          )}
          <MenuItem
            disabled={!actionItemState.canFlagDevice}
            onClick={() => {
              updateDialogState('FLAG_DEVICE');
              setIsOpen(false);
            }}
          >
            <FlagOutlined className={classes.icon} />
            Flag device
          </MenuItem>
          <MenuItem
            disabled={!actionItemState.canPinDevice}
            onClick={() => {
              updateDialogState('PIN_DEVICE');
              setIsOpen(false);
            }}
          >
            <VerticalAlignTop className={classes.icon} />
            {isAnyUnpinned ? 'Pin' : 'Unpin'} device
          </MenuItem>
          <MenuItem
            disabled={!actionItemState.canArchiveDevice}
            onClick={() => {
              updateDialogState('ARCHIVE_DEVICE');
              setIsOpen(false);
            }}
          >
            <Archive className={classes.icon} />
            {isAnyUnarchived ? 'Archive' : 'Unarchive'} device
          </MenuItem>
          <MenuItem
            disabled={!actionItemState.canMuteDevice}
            onClick={() => {
              updateDialogState('MUTE_DEVICE');
              setIsOpen(false);
            }}
          >
            <RemoveCircleOutline color="inherit" className={classes.icon} />
            {isAnyMuted ? 'Unmute' : 'Mute'} device
          </MenuItem>
          {/*</VisibleTo>*/}

          <MenuItem disabled={!actionItemState.canTestDevice}>
            <TestDeviceSwitch
              devices={devices}
              onTestDeviceChange={(test, err) => {
                if (err) {
                  enqueueSnackbar(err, { variant: 'error' });
                  return;
                }
                const txt = test
                  ? 'Device is marked as test device'
                  : 'Device is no longer marked as test device';
                enqueueSnackbar(txt, { variant: 'info' });
              }}
            />
          </MenuItem>

          <MenuItem
            disabled={!actionItemState.canDeleteDevice}
            onClick={() => {
              updateDialogState('DELETE_DEVICE');
              setIsOpen(false);
            }}
          >
            <Delete className={classes.icon} />
            Delete device
          </MenuItem>

          <MenuItem
            disabled={!actionItemState.canResetCount || totalMeasurementMode}
            onClick={() => {
              updateDialogState('RESET_COUNT');
              setIsOpen(false);
            }}
          >
            <Replay className={classes.icon} />
            Reset count
          </MenuItem>

          <MenuItem
            disabled={!actionItemState.canBlock}
            onClick={() => {
              updateDialogState('BLOCK_DEVICE');
              setIsOpen(false);
            }}
          >
            <Block className={classes.icon} />
            {isBlocked ? 'Unblock' : 'Block'} device
          </MenuItem>

          <MenuItem disabled={!actionItemState.canStockDevice}>
            <StockDeviceSwitch
              devices={devices}
              onStockDeviceChange={(test, err) => {
                if (err) {
                  enqueueSnackbar(err, { variant: 'error' });
                  return;
                }
                const txt = test
                  ? 'Device is marked in stock.'
                  : 'Device is no longer marked in stock.';
                enqueueSnackbar(txt, { variant: 'info' });
              }}
            />
          </MenuItem>

          <MenuItem disabled={!actionItemState.canUnlinkedGateway}>
            <UnlinkedGatewaySwitch
              devices={devices}
              onUnlinkedChange={(allow, err) => {
                if (err) {
                  enqueueSnackbar(err, { variant: 'error' });
                  return;
                }
                const txt = allow
                  ? 'Gateway can no longer be unlinked.'
                  : 'Gateway can now be unlinked.';
                enqueueSnackbar(txt, { variant: 'info' });
              }}
            />
          </MenuItem>

          <Divider />

          <ExportToCsv
            devices={devices}
            onExport={() => {
              updateDialogState('CLOSE_DIALOG');
              setIsOpen(false);
            }}
          />
          <MenuItem
            onClick={() => {
              console.log('CLicked');
              let txt = '';
              devices.forEach((d) => {
                txt += `Model: ${d.model}\nSerial Number: ${
                  d.serialNumber
                }\nIMEI: ${d.imei ?? '-'}\nICCID: ${d.iccid ?? '-'}\nMAC: ${
                  d.mac ?? '-'
                }\n\n`;
              });
              navigator.clipboard.writeText(txt);
              enqueueSnackbar('Copied to clipboard', { variant: 'info' });
            }}
          >
            <FileCopy className={classes.icon} />
            Copy to clipboard
          </MenuItem>
        </Menu>
      </>
    );
  }
);

export default ActionsMenu;
